import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { getDashboardPulse } from '../../../services/DashboardOverviewService';
import { useChartTheme } from '../../../utils/chartTheme';
import { Skeleton, SkeletonGroup } from '../common/Skeleton/Skeleton.jsx';
import { silenceFor } from '../common/Loader/busy';

// Only the pieces this chart uses. Registering the whole bundle pulls in
// every scale and controller Chart.js ships.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

// Which chart-theme series slot each rate wears (utils/chartTheme): slot 1
// is the palette's accent, the rest are fixed hues chosen to stay apart
// from it and from each other. Keyed by the rate, not by its position in
// the chart, so a rate that reports nothing this period does not shift the
// others onto new colours -- colour follows the measure, never the rank.
const SERIES_SLOT = {
  attendanceRate: 0,
  assessmentRate: 1,
  interviewRate: 2,
  trainerActiveRate: 3,
};

// Speedometer geometry, the same as the trainer dashboard's: a half-circle
// of radius 80 swept left-to-right over the top, so the arc length is half a
// circumference, not a whole one. Track and fill share the path so they
// cannot drift apart.
const GAUGE_R = 80;
const GAUGE_ARC = `M 20 100 A ${GAUGE_R} ${GAUGE_R} 0 0 1 180 100`;
const GAUGE_LEN = Math.PI * GAUGE_R;

const healthBand = (n) => {
  if (n === null || n === undefined) return 'No data';
  if (n >= 75) return 'Strong';
  if (n >= 50) return 'Fair';
  return 'Needs work';
};

const trendWord = (n) => {
  if (n === null || n === undefined) return 'no comparison yet';
  if (n > 0) return `up ${n}%`;
  if (n < 0) return `down ${Math.abs(n)}%`;
  return 'flat';
};

const trendTone = (n) => {
  if (n === null || n === undefined) return '';
  if (n > 0) return ' is-up';
  if (n < 0) return ' is-down';
  return '';
};

const BusinessPulse = ({ user, getDashboardTitle }) => {
  const theme = useChartTheme();
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    (async () => {
      try {
        // The box shows its own skeleton, so the global overlay stays down.
        silenceFor();
        const res = await getDashboardPulse(6);
        setPulse(res?.data || null);
      } catch (_) {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const months = pulse?.months || [];
  const rates = pulse?.rates || [];
  const headline = pulse?.headline || {};

  // A series is drawn only if it reported at least one month. A rate the
  // database cannot answer is absent from the chart and named underneath,
  // rather than drawn as a line along zero — which would read as "nobody
  // attended" instead of "attendance is not recorded". Whether a rate
  // reported is the API's call (rates[].reported); the box only sorts the
  // two lists onto the chart and under it.
  const { reported, missing } = useMemo(
    () => ({
      reported: rates.filter((r) => r.reported),
      missing: rates.filter((r) => !r.reported),
    }),
    [rates],
  );

  const chartData = useMemo(
    () => ({
      labels: months.map((m) => m.label),
      datasets: reported.map((r) => {
        const colour = theme.series[SERIES_SLOT[r.key] ?? 0];
        return {
          label: r.label,
          data: months.map((m) => m[r.key]),
          borderColor: colour,
          backgroundColor: colour,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          // The 2px surface ring that keeps a point legible where lines cross.
          pointBorderWidth: 2,
          pointBorderColor: theme.surface,
          tension: 0.3,
          // A month with no reading breaks the line rather than being joined
          // through, so a gap reads as a gap.
          spanGaps: false,
        };
      }),
    }),
    [months, reported, theme],
  );

  // One series, so slot 1 -- the palette's accent -- with its area as a 10%
  // wash of the same hue rather than a saturated block.
  const growthData = useMemo(
    () => ({
      labels: months.map((m) => m.label),
      datasets: [
        {
          label: 'New enrolments',
          data: months.map((m) => m.enrolled),
          borderColor: theme.series[0],
          backgroundColor: theme.wash(theme.series[0], 0.1),
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: theme.series[0],
          pointBorderWidth: 2,
          pointBorderColor: theme.surface,
          tension: 0.3,
          fill: true,
        },
      ],
    }),
    [months, theme],
  );

  const growthOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...theme.tooltip,
          callbacks: {
            label: (ctx) =>
              ` ${ctx.parsed.y} student${ctx.parsed.y === 1 ? '' : 's'} enrolled`,
          },
        },
      },
      scales: {
        x: theme.categoryAxis({ ticks: { color: theme.ink.faint } }),
        y: theme.valueAxis({ beginAtZero: true, ticks: { precision: 0 } }),
      },
    }),
    [theme],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...theme.tooltip,
          // Four lines share the tooltip, so each row carries its swatch.
          displayColors: true,
          boxWidth: 8,
          boxHeight: 8,
          callbacks: {
            label: (ctx) =>
              ` ${ctx.dataset.label}: ${ctx.parsed.y === null ? 'no data' : `${ctx.parsed.y}%`}`,
          },
        },
      },
      scales: {
        x: theme.categoryAxis({ ticks: { color: theme.ink.faint } }),
        // One axis. Every series is a percentage, so they share it honestly;
        // the enrolment count lives beside the chart as its own figure
        // rather than on a second scale.
        y: theme.valueAxis({
          min: 0,
          max: 100,
          ticks: { stepSize: 25, callback: (v) => `${v}%` },
        }),
      },
    }),
    [theme],
  );

  return (
    <div className="bp">
      {/* Who the dashboard is for, and the one number that answers "how are
          we doing" before anything else is read. */}
      <section className="bp-hero">
        <span className="bp-hero-eyebrow">Administration</span>
        <h1 className="bp-hero-title">{getDashboardTitle(user)}</h1>

        <div className="bp-hero-figure">
          <span className="bp-hero-label">Health score</span>

          <div
            className="bp-gauge"
            role="img"
            aria-label={`Health score ${headline.health ?? 0} out of 100`}
          >
            <svg viewBox="0 0 200 116" aria-hidden="true">
              <path className="bp-gauge-track" d={GAUGE_ARC} />
              <path
                className="bp-gauge-fill"
                d={GAUGE_ARC}
                strokeDasharray={GAUGE_LEN}
                strokeDashoffset={GAUGE_LEN * (1 - (headline.health ?? 0) / 100)}
              />
            </svg>
            <div className="bp-gauge-face">
              {loading ? (
                <Skeleton tone="dark" shape="figure" width="55" />
              ) : (
                <>
                  <b>{headline.health === null || headline.health === undefined ? '—' : headline.health}</b>
                  <small>{healthBand(headline.health)}</small>
                </>
              )}
            </div>
            <div className="bp-gauge-scale" aria-hidden="true">
              <span>0</span>
              <span>100</span>
            </div>
          </div>

          <p className="bp-hero-note">
            The mean of every rate that reported this month.
          </p>
        </div>

        {/* While the pulse is out, the four figures shimmer rather than
            reading 0 -- a zero here says "nothing happened this month". */}
        <dl className="bp-hero-facts">
          <div>
            <dt>Enrolled this month</dt>
            <dd>
              {loading ? (
                <Skeleton tone="dark" shape="title" width="40" />
              ) : (
                <>
                  {headline.enrolledThisMonth ?? 0}
                  <span className={`bp-delta${trendTone(headline.growth)}`}>
                    {trendWord(headline.growth)}
                  </span>
                </>
              )}
            </dd>
          </div>
          <div>
            <dt>Trainers</dt>
            <dd>{loading ? <Skeleton tone="dark" shape="title" width="40" /> : (headline.totalTrainers ?? 0)}</dd>
          </div>
          <div>
            <dt>Batches</dt>
            <dd>{loading ? <Skeleton tone="dark" shape="title" width="40" /> : (headline.totalBatches ?? 0)}</dd>
          </div>
          <div>
            <dt>Job applications</dt>
            <dd>{loading ? <Skeleton tone="dark" shape="title" width="40" /> : (headline.applicationsThisMonth ?? 0)}</dd>
          </div>
        </dl>
      </section>

      {/* The rates over six months, on one 0-100 axis. */}
      <section className="bp-chart">
        <header className="bp-chart-head">
          <div>
            <h2 className="bp-chart-title">How the last six months went</h2>
            <p className="bp-chart-sub">
              Every line is a share of what was attempted, month by month.
            </p>
          </div>
        </header>

        {loading ? (
          <SkeletonGroup label="Loading the last six months" className="lms-skel-stack">
            <Skeleton width="55" />
            <Skeleton shape="chart" />
          </SkeletonGroup>
        ) : failed ? (
          <div className="bp-chart-state">That did not load. Try refreshing.</div>
        ) : reported.length === 0 ? (
          <div className="bp-chart-state">
            None of these rates has data yet.
          </div>
        ) : (
          <>
            {/* Identity is never colour alone: the legend names each series
                beside its own swatch. */}
            <ul className="bp-legend">
              {reported.map((r) => (
                <li key={r.key}>
                  <span
                    className="bp-legend-dot"
                    style={{ background: theme.series[SERIES_SLOT[r.key] ?? 0] }}
                    aria-hidden="true"
                  />
                  {r.label}
                </li>
              ))}
            </ul>

            <div className="bp-chart-canvas">
              <Line data={chartData} options={options} />
            </div>
          </>
        )}

        {/* Enrolments are a count, not a share, so they get their own panel
            rather than a second scale on the chart above — two y-axes let a
            reader compare two things that do not share units. Same months
            across the bottom, so the two read as one figure. */}
        {months.length > 0 && (
          <div className="bp-sub">
            <div className="bp-sub-head">
              <h3 className="bp-sub-title">Student growth</h3>
              <span className="bp-sub-note">New enrolments per month</span>
            </div>
            <div className="bp-sub-canvas">
              <Line data={growthData} options={growthOptions} />
            </div>
          </div>
        )}

        {missing.length > 0 && (
          <p className="bp-chart-missing">
            Not charted, because the records these are counted from do not
            exist yet: {missing.map((r) => r.label).join(', ')}.
          </p>
        )}

        {/* The figures behind the lines, for anyone who would rather read
            them than hover. */}
        {reported.length > 0 && (
          <details className="bp-table">
            <summary>Show the figures</summary>
            <div className="bp-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Month</th>
                    {reported.map((r) => (
                      <th key={r.key}>{r.label}</th>
                    ))}
                    <th>Enrolled</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => (
                    <tr key={m.key}>
                      <td>{m.label}</td>
                      {reported.map((r) => (
                        <td key={r.key}>
                          {m[r.key] === null || m[r.key] === undefined
                            ? '—'
                            : `${m[r.key]}%`}
                        </td>
                      ))}
                      <td>{m.enrolled}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </section>
    </div>
  );
};

export default BusinessPulse;
